import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useToast } from '@rbilabs/universal-components';
import cloneDeep from 'lodash/cloneDeep';
import { useIntl } from 'react-intl';
import uuidv4 from 'uuid/v4';

import { IBackendCartEntries, ICartEntry, IItem } from '@rbi-ctg/menu';
import { ICartEntryFragment, OfferType } from 'generated/rbi-graphql';
import useAuth from 'hooks/auth/use-auth';
import { getPosVendorFromStore } from 'hooks/menu/use-pos-vendor';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { usePriceOrder } from 'hooks/price-order';
import useDialogModal from 'hooks/use-dialog-modal';
import { useTotalTipCalculations } from 'pages/cart/your-cart/totals/use-total-tip-calculations';
import {
  findEntriesForSwap,
  mapEntryTypeToOfferType,
  replaceEntryForSwap,
} from 'state/cart/helpers';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { isDiscountOffer } from 'state/loyalty/utils';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { IAddToCartSelectionAttributes } from 'state/mParticle/types';
import { useMenuContext } from 'state/menu';
import { OrderStatus, ServiceMode, useOrderContext } from 'state/order';
import { useUnavailableCartEntries } from 'state/order/hooks/use-unavailable-cart-entries';
import { usePaymentContext } from 'state/payment';
import { useStoreContext } from 'state/store';
import { createCartEntry } from 'utils/cart';
import { redeemReward } from 'utils/cart/redeem-reward';
import { EventName, emitEvent } from 'utils/event-hub';
import { AttributeName } from 'utils/performance';
import { routes } from 'utils/routing';
import { isDelivery } from 'utils/service-mode';

import { useSanitizedRewards } from './hooks/use-sanitized-rewards';
import type { ICartContext, ICartContextArgs, ISwappableItem, ITryPricingOverrides } from './types';

const CartContext = createContext<ICartContext>({} as ICartContext);

export const CartProvider: React.FC<React.PropsWithChildren<ICartContextArgs>> = ({ children }) => {
  const { navigate } = useNavigation();
  const [cardSuccessfullyAdded, setCardSuccessfullyAdded] = useState(false);

  const [shouldSkipPriceOrder, setShouldSkipPriceOrder] = useState(false);

  // TODO: RTK - 2
  const {
    cartEntries: orderContextCartEntries,
    cartVersion,
    calculateCartTotalWithDiscount,
    deliveryAddress,
    deliveryInstructions,
    emptyCart: orderEmptyCart,
    orderPhoneNumber,
    serviceMode: orderServiceMode,
    selectServiceMode: selectOrderServiceMode,
    setCartEntries: setOrderCartEntries,
    repriceCartEntries,
  } = useOrderContext();
  const mParticle = useMParticleContext();

  const { checkoutPaymentMethod } = usePaymentContext();

  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);
  const isDriveThruHidden = isFastLaneEnabled && orderServiceMode === ServiceMode.DRIVE_THRU;
  const initialServiceMode = isDriveThruHidden ? ServiceMode.TAKEOUT : orderServiceMode;
  const [serviceMode, setServiceMode] = useState(initialServiceMode);
  // Keep local ref of the serviceMode to properly update OrderContext on unmount
  // The ref update is synchronous, while the state update will only occur after re-render
  const contextServiceModeRef = useRef(initialServiceMode);

  const loyaltyEnabled = useIsLoyaltyEnabled();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  const [contextCartEntries, setContextCartEntries] = useState<ICartEntry[]>(
    orderContextCartEntries
  );
  const toast = useToast();

  const selectServiceMode = useCallback(
    (selectedServiceMode: ServiceMode) => {
      setServiceMode(selectedServiceMode);
      contextServiceModeRef.current = selectedServiceMode;
    },
    [setServiceMode]
  );

  const { user, refreshCurrentUser } = useAuth();
  const { isStoreOpenAndAvailable, store, serviceModeStatus } = useStoreContext();
  const { formatMessage } = useIntl();
  const { pricingFunction } = useMenuContext();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const loyaltyAppliedDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );
  const isPricingRewardApplication = useAppSelector(
    selectors.loyalty.selectIsPricingRewardApplication
  );

  const { locale: customerLocale } = useLocale();
  const dispatch = useAppDispatch();

  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();

  useEffect(() => {
    // TODO: having a deps array will trigger this cleanup function every time that the deps changes
    // Check if this is intentional or this should only be called on unmount
    return () => {
      if (deliveryAddress?.shouldSave) {
        refreshCurrentUser();
      }
    };
  }, [deliveryAddress, refreshCurrentUser]);

  useEffect(() => {
    // Update OrderContext on unmount since Cart components should use serviceMode from CartContext
    // This significantly improves perceived performance when switching between serviceMode options
    return () => {
      if (contextServiceModeRef.current !== orderServiceMode) {
        selectOrderServiceMode(contextServiceModeRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Ensure the service mode is being reset if it's Delivery
    // This happens in React Nav when changing stores from the Cart between Pick Up/Delivery that may result in a stale state
    const isDeliveryOrder =
      isDelivery(orderServiceMode) || isDelivery(contextServiceModeRef.current);
    if (isDeliveryOrder && contextServiceModeRef.current !== orderServiceMode) {
      selectServiceMode(orderServiceMode);
    }
  }, [orderServiceMode, selectServiceMode]);

  const appliedOffersMap = useMemo(
    () =>
      appliedOffers.reduce((acc, offer) => {
        acc[offer.cartId || ''] = offer;
        return acc;
      }, {}),
    [appliedOffers]
  );

  const cartEntries = useMemo(() => {
    // Create the cart entry from the selected discount offer
    // This offer will be available if the cart is not empty
    const selectedOffer = loyaltyAppliedDiscountOffer;

    // @ts-expect-error TS(2322) FIXME: ICartEnty does not support empty vendorConfigs
    const offerDiscountCartEntry: ICartEntry[] =
      contextCartEntries.length && selectedOffer && isDiscountOffer(selectedOffer)
        ? [
            {
              ...createCartEntry({ item: selectedOffer }),
              cartId: 'discount-offer',
              vendorConfigs: undefined,
              offerVendorConfigs: selectedOffer.vendorConfigs,
            },
          ]
        : [];

    // Updating entry type if its part of an applied offer
    const updatedCartEntries = (contextCartEntries || []).map((entry: ICartEntry) =>
      appliedOffersMap[entry.cartId]
        ? { ...entry, type: mapEntryTypeToOfferType(entry.type) }
        : entry
    );

    // Cart entries with offers
    return repriceCartEntries(updatedCartEntries.concat(offerDiscountCartEntry));
  }, [appliedOffersMap, loyaltyAppliedDiscountOffer, contextCartEntries, repriceCartEntries]);

  const rewardsApplied = useSanitizedRewards({ cartEntries });

  const swapItems = useCallback(
    (to: IItem, swapOffer: ISwappableItem) => {
      const { from, ...offer } = swapOffer;
      setContextCartEntries(prevEntries => {
        // Root cart entry is needed for appliedOffers
        // Child cart entry is needed for swaps objects
        const { rootEntry, childEntry } = findEntriesForSwap(prevEntries, from);
        let entriesToReturn = prevEntries;

        if (rootEntry) {
          const oldEntry = rootEntry;
          // Define price for item
          const oldEntryUnitaryPrice = (oldEntry?.price ?? 0) / oldEntry.quantity;
          const { type: swapType, offerId, offerType, cmsId } = offer;

          const entryToSwap = {
            ...createCartEntry({
              item: to,
              price: oldEntry.children.length ? 0 : oldEntryUnitaryPrice,
              quantity: 1,
            }),
            sanityId: to._id,
          };

          const offerToApply = {
            id: offerId,
            cartId: oldEntry.children.length ? oldEntry.cartId : entryToSwap.cartId,
            type: OfferType[offerType],
            swap: {
              cartId: entryToSwap.cartId,
              from,
              to: to._id,
              swapType,
            },
            cmsId,
          };

          // This covers the case when a cartEntry has more than one item and swap is selected
          // updating price and quantity to the cartEntry that was swapped
          if (oldEntry.quantity > 1) {
            oldEntry.quantity--;
            oldEntry.price = oldEntryUnitaryPrice * oldEntry.quantity;

            // If cartEntry has children, we keep a copy of the entry without
            // the swap applied to concat to the entries array after applying the swap
            if (oldEntry.children.length) {
              const splittedItem = cloneDeep(oldEntry);
              splittedItem.cartId = uuidv4();
              offerToApply.cartId = oldEntry.cartId;
              // Creating new cart entry with swap applied to replace at root level
              // having quantity and price correct for a swapped item
              const newRoot = {
                ...oldEntry,
                children: replaceEntryForSwap(oldEntry.children, from, {
                  ...entryToSwap,
                  price: 0,
                }),
                price: oldEntryUnitaryPrice,
                quantity: 1,
              };

              entriesToReturn = replaceEntryForSwap(prevEntries, oldEntry._id, newRoot).concat(
                splittedItem
              );
            } else {
              entriesToReturn = prevEntries.concat(entryToSwap);
            }
          } else {
            entriesToReturn = replaceEntryForSwap(prevEntries, from, entryToSwap);
          }

          dispatch(actions.loyalty.applyOffer(offerToApply));

          mParticle.logEvent(CustomEventNames.SWAP_OFFER_USED, EventTypes.Other, {
            'Original Item Name': childEntry?.name,
            'Original Item Sanity ID': from,
            'Swapped Item Name': to.name?.locale,
            'Swapped Item Sanity ID': to._id,
            'Systemwide Offer ID': offer.offerId,
          });
        }

        return entriesToReturn;
      });
    },
    [setContextCartEntries, dispatch, mParticle]
  );
  const {
    priceOrder,
    orderStatus,
    priceOrderFailure,
    priceOrderSuccess,
    priceResult: { error: priceMutationError },
    serverOrder,
  } = usePriceOrder({
    cartEntries,
  });

  const restaurantPosDataId = store.restaurantPosData?._id ?? '';
  const storeNumber = store.number ?? '';
  const vendor = getPosVendorFromStore(store, serviceMode);
  const { unavailableCartEntries, setUnavailableCartEntries } = useUnavailableCartEntries({
    cartEntries,
    serverOrder,
    restaurantPosDataId,
    storeNumber,
    vendor,
  });

  // To access the Cart we should have OrderCtx cartEntries
  // If those are empty is because it was cleared out by an external cart logic (reset cart time / order commit)
  const orderContextCartEntriesWasCleared = !orderContextCartEntries?.length;

  // Curbside will not have INSERT_SUCCESSFUL status if we close the cart before clicking I'm Here button
  const orderWasPlaced = orderStatus === OrderStatus.INSERT_SUCCESSFUL;

  /**
   * CartContext has its own local state.
   * Storing the deps on refs so we don't have stale values on the useEffect cleanup function.
   */
  const cartEntriesRef = useRef<ICartEntry[]>();
  cartEntriesRef.current = contextCartEntries;

  const skipOrderContextSyncRef = useRef<boolean>();
  skipOrderContextSyncRef.current = orderContextCartEntriesWasCleared || orderWasPlaced;

  const orderEmptyCartRef = useRef<() => {}>();
  orderEmptyCartRef.current = orderEmptyCart;

  /**
   * Sync cartEntries with OrderContext
   */
  useEffect(() => {
    if (skipOrderContextSyncRef.current) {
      return;
    }
    // Update order context setting all local state changes (edit, update, remove..)
    if (!cartEntriesRef.current?.length) {
      orderEmptyCartRef.current?.();
    } else {
      setOrderCartEntries(cartEntriesRef.current);
    }
  }, [contextCartEntries, orderEmptyCart, setOrderCartEntries]);

  const { rewardDiscountAmount = 0, additionalDiscountAmount = 0 } = useTotalTipCalculations({
    serverOrder,
    serviceMode,
  });

  const addItemToCart = useCallback(
    (
      cartEntry: ICartEntry | IBackendCartEntries | ICartEntryFragment | null,
      eventAttrs: IAddToCartSelectionAttributes | undefined,
      showMessage = true
    ) => {
      setContextCartEntries((prevCartEntries: ICartEntry[]) => {
        mParticle.logAddOrRemoveFromCart({
          action: 'add',
          cartEntry: cartEntry as ICartEntry,
          previousCartEntries: prevCartEntries,
          selectionAttrs: eventAttrs,
        });
        return prevCartEntries.concat([cartEntry as ICartEntry]);
      });

      if (cartEntry?.isDonation) {
        return;
      }

      if (showMessage) {
        toast.show({
          text: formatMessage({ id: 'addToCartSuccess' }, { itemName: cartEntry?.name }),
          variant: 'positive',
        });
      }
    },
    [formatMessage, mParticle, toast]
  );

  const removeRewardIfNeeded = useCallback(
    (cartEntry: ICartEntry) => {
      const { cartId } = cartEntry;
      const cartEntryReward = getAvailableRewardFromCartEntry(cartEntry);

      const isRewardApplied = !!appliedLoyaltyRewards?.[cartId]?.timesApplied;
      if (loyaltyEnabled && isRewardApplied && cartEntryReward) {
        dispatch(
          actions.loyalty.removeAppliedReward({
            rewardBenefitId: cartEntryReward.rewardBenefitId,
            cartId,
          })
        );
      }
    },
    [appliedLoyaltyRewards, getAvailableRewardFromCartEntry, loyaltyEnabled, dispatch]
  );

  const shouldEmptyCart = useCallback(
    ({ cartId }: { cartId: string }) => {
      const appliedOffersMap = appliedOffers.reduce((acc, appliedOffer) => {
        if (appliedOffer.cartId) {
          acc[appliedOffer.cartId] = appliedOffer;
        }
        return acc;
      }, {});

      const entriesNotRemoved = contextCartEntries.filter(
        (entry: ICartEntry) => entry.cartId !== cartId
      );

      return entriesNotRemoved.every(
        (entry: ICartEntry) =>
          entry.isDonation || appliedOffersMap[entry?.cartId]?.isSurprise || entry?.isExtra
      );
    },
    [appliedOffers, contextCartEntries]
  );

  const logCartEntryRemovedFromCart = useCallback(
    (cartEntry: ICartEntry, previousCartEntries: ICartEntry[]) => {
      mParticle.logAddOrRemoveFromCart({
        action: 'remove',
        cartEntry,
        previousCartEntries,
      });
    },
    [mParticle]
  );

  const removeFromCart = useCallback(
    ({ cartId }: { cartId: string }) => {
      const cartEntryToRemove = contextCartEntries.find(
        (entry: ICartEntry) => entry.cartId === cartId
      );

      if (!cartEntryToRemove) {
        return;
      }
      logCartEntryRemovedFromCart(cartEntryToRemove, contextCartEntries);
      dispatch(actions.loyalty.removeAppliedOfferByCartEntry(cartEntryToRemove));
      setContextCartEntries((prevCartEntries: ICartEntry[]) =>
        prevCartEntries.filter(entry => cartEntryToRemove.cartId !== entry.cartId)
      );

      // removes applied rewards associated to cart entry on removal
      removeRewardIfNeeded(cartEntryToRemove);

      // Resets the availability of Surprise so it wont show in cart page
      dispatch(actions.loyalty.resetSurpriseAvailability());
    },
    [contextCartEntries, dispatch, logCartEntryRemovedFromCart, removeRewardIfNeeded]
  );

  const emptyCart = () => {
    setContextCartEntries([]);
    cartEntriesRef.current = [];
  };

  const removeAllFromCart = useCallback(
    (cartEntriesToRemove: ICartEntry[] = []): void => {
      cartEntriesToRemove.forEach(entry => removeFromCart(entry));
    },
    [removeFromCart]
  );

  const onConfirmRemoveItemDialog = useCallback(
    (entry: any) => {
      emitEvent(EventName.CART_CONTENT_UPDATE, {
        attributes: [
          {
            name: AttributeName.CART_UPDATE_SOURCE,
            value: 'remove_item',
          },
        ],
      });
      return shouldEmptyCart(entry) ? setContextCartEntries([]) : removeFromCart(entry);
    },
    [removeFromCart, shouldEmptyCart]
  );

  const [RemoveItemDialog, openRemoveItemDialog, itemToRm] = useDialogModal({
    onConfirm: onConfirmRemoveItemDialog,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Remove item from cart',
  });

  // for confirming item removal
  const confirmRemoveItemFromCart = useCallback(
    (cartId: string) => {
      const entryToRemove = contextCartEntries.find((item: ICartEntry) => cartId === item.cartId);
      openRemoveItemDialog(entryToRemove);
    },
    [contextCartEntries, openRemoveItemDialog]
  );

  const updateItemsQuantity = useCallback(
    (cartId: string, quantity: number) => {
      if (quantity < 1) {
        return removeFromCart({ cartId });
      }

      setContextCartEntries((prevCartEntries: ICartEntry[]) =>
        prevCartEntries.map(entry => {
          if (entry.cartId === cartId) {
            entry.quantity = quantity;
            entry.price = pricingFunction({ item: entry, quantity: entry.quantity });
          }

          return entry;
        })
      );
    },
    [pricingFunction, removeFromCart]
  );

  const { cartTotal, isCartTotalNegative } = calculateCartTotalWithDiscount(cartEntries);
  const totalAppliedDiscount = rewardDiscountAmount + additionalDiscountAmount;

  // Reward discounts are already applied to items causing the requested amount to be less than the real amount since
  // We are substracting the discount twice (on the item and again on the discount)
  const requestedAmountCents = cartTotal - totalAppliedDiscount + rewardDiscountAmount;

  const customerName =
    isFastLaneEnabled && serviceMode === ServiceMode.TAKEOUT ? `FL-${user?.details?.name}` : null;

  const tryPricing = useCallback(
    (overrideOrderParams?: ITryPricingOverrides) => {
      if (!isStoreOpenAndAvailable || !user || redeemReward || isCartTotalNegative) {
        return;
      }

      const validAppliedOffers = appliedOffers.map(({ id, cartId, swap, type, cmsId }) => ({
        id,
        cartId,
        swap,
        type,
        sanityId: cmsId,
      }));

      const orderParams = {
        rewardsApplied,
        cartVersion,
        customerLocale,
        customerName,
        deliveryAddress,
        orderPhoneNumber,
        deliveryInstructions,
        appliedOffers: validAppliedOffers,
        requestedAmountCents,
        serviceMode,
        store,
        isStoreOpenAndAvailable,
        ...overrideOrderParams,
      };

      void priceOrder(orderParams, user);
      if (isPricingRewardApplication) {
        dispatch(actions.loyalty.setIsPricingRewardApplication(false));
      }
    },
    [
      additionalDiscountAmount,
      rewardsApplied,
      appliedOffers,
      calculateCartTotalWithDiscount,
      cartVersion,
      checkoutPaymentMethod,
      customerLocale,
      customerName,
      deliveryAddress,
      deliveryInstructions,
      dispatch,
      isPricingRewardApplication,
      isStoreOpenAndAvailable,
      orderPhoneNumber,
      priceOrder,
      rewardDiscountAmount,
      serviceMode,
      store,
      user,
      requestedAmountCents,
    ]
  );

  const removeItemMessage = (item: ICartEntry) => {
    return item
      ? formatMessage(
          { id: 'removeItemFromCart' },
          {
            item: item.name,
            offerText: '',
          }
        )
      : '';
  };

  const pricedAndAvailable = useMemo(() => {
    return priceOrderSuccess && !unavailableCartEntries.length && !priceMutationError;
  }, [priceMutationError, priceOrderSuccess, unavailableCartEntries.length]);

  const enableFutureOrdering = useFlag(LaunchDarklyFlag.ENABLE_FUTURE_ORDERING);

  const selectedDonationCartEntry = useMemo(
    () => cartEntries.find((cartEntry: ICartEntry) => cartEntry.isDonation),
    [cartEntries]
  );

  // This is important to let the reset cart time out works as we expect when the user is on the cart screen.
  // as the empty cart method callback is fired on the order provider and doesn't has effect on it, since we are using a ref to keep the cart items.
  // In order to empty the cart in that case we need to fire the emptyCart from here.
  useEffect(() => {
    if (orderContextCartEntriesWasCleared) {
      emptyCart();
      navigate(routes.base);
    }
  }, [navigate, orderContextCartEntriesWasCleared]);

  return (
    <CartContext.Provider
      value={{
        cartEntries,
        addItemToCart,
        confirmRemoveItemFromCart,
        removeFromCart,
        removeAllFromCart,
        emptyCart,
        updateItemsQuantity,
        serverOrder,
        tryPricing,
        cardSuccessfullyAdded,
        setCardSuccessfullyAdded,
        orderStatus,
        priceOrderFailure,
        priceOrderSuccess,
        priceMutationError,
        pricedAndAvailable,
        unavailableCartEntries,
        setUnavailableCartEntries,
        serviceMode,
        selectServiceMode,
        serviceModeStatus,
        shouldSkipPriceOrder,
        setShouldSkipPriceOrder,
        enableFutureOrdering,
        selectedDonationCartEntry,
        swapItems,
        requestedAmountCents,
      }}
    >
      {children}

      <RemoveItemDialog
        body={removeItemMessage(itemToRm)}
        heading={formatMessage({ id: 'removeItem' })}
      />
    </CartContext.Provider>
  );
};

export const useCartContext = () => useContext(CartContext);
